import React, { useEffect, useState } from 'react';

import { ActionIcon, Box, Center, Loader, Stack, Text, createStyles } from '@mantine/core';
import { IconDotsVertical, IconUsers } from '@tabler/icons-react';
import { handleApiError } from 'api/error-handling';
import KiloButton from 'components/KiloButton/KiloButton';
import dayjs from 'dayjs';
import useAthlete from 'hooks/athlete/useAthlete';
import { type ProgrammingCalendarView, useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useAssignGate from 'hooks/workout/useAssignGate';
import useDeleteWorkout from 'hooks/workout/useDeleteWorkout';
import useToggleWorkoutVisibility from 'hooks/workout/useToggleWorkoutVisibility';
import { useNavigate } from 'react-router-dom';
import type { AthleteList } from 'types';
import { getWeekRange } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';

import AthleteCalendarHeader from './AthleteCalendarHeader';
import AthleteDayView from './AthleteDayView';
import AthleteEventDrawer from './AthleteEventDrawer';
import AthleteInfoBar from './AthleteInfoBar';
import AthleteMonthView from './AthleteMonthView';
import AthletePanel, { PANEL_WIDTH } from './AthletePanel';
import AthleteWeekView from './AthleteWeekView';
import type {
  AthleteCalendarAthlete,
  AthleteCalendarEventItem,
  AthleteCalendarFilters,
  AthleteCalendarItem,
  AthleteCalendarWorkoutItem,
} from './types';
import useAthleteCalendar from './useAthleteCalendar';
import AssignWorkoutModal from '../AssignWorkoutModal';
import DeleteWorkoutModal from '../DeleteWorkoutModal';
import { ProgramCalendarView, ProgrammingNavState } from '../types';
import WorkoutAssignmentMenu from '../WorkoutAssignmentMenu';
import WorkoutPreviewModal from '../WorkoutPreviewModal';

const PANEL_ANIMATION_MS = 300;

type AssignSource = 'info_bar' | 'day_view' | 'week_view' | 'month_view' | 'change_workout';
/** App header, page title and the tab bar above this component. */
const TAB_CHROME_HEIGHT = 100;

const useStyles = createStyles(() => ({
  layout: {
    display: 'flex',
    alignItems: 'stretch',
    height: `calc(100vh - ${TAB_CHROME_HEIGHT}px)`,
    overflow: 'hidden',
  },
  panelSlot: {
    display: 'flex',
    overflow: 'hidden',
    transition: `width ${PANEL_ANIMATION_MS}ms ease`,
  },
  calendar: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 },
  // The info bar and the calendar header stay pinned; only the view scrolls.
  calendarBody: { flex: 1, minHeight: 0, overflowY: 'auto' },
}));

/** The Mobiscroll month grid spans whole weeks, so the read covers the same days. */
const rangeForView = (view: ProgramCalendarView, date: string): { start: string; end: string } => {
  if (view === 'week') return getWeekRange(date);
  if (view === 'agenda') return { start: date, end: date };
  const month = dayjs(date);
  return {
    start: month.startOf('month').startOf('week').format('YYYY-MM-DD'),
    end: month.endOf('month').endOf('week').format('YYYY-MM-DD'),
  };
};

const periodLabelForView = (view: ProgramCalendarView, date: string): string => {
  const day = dayjs(date);
  if (view === 'month') return day.format('MMMM YYYY');
  if (view === 'agenda') return day.format('dddd, MMMM D');
  const { start, end } = getWeekRange(date);
  return `${dayjs(start).format('MMMM D')} – ${dayjs(end).format('MMMM D')}`;
};

const viewLabel = (value: ProgramCalendarView): ProgrammingCalendarView =>
  value === 'agenda' ? 'day' : value;

const packageNames = (athlete: AthleteList): string | null =>
  athlete.currentPackages.map(currentPackage => currentPackage.name).join(', ') || null;

const stepForView = (view: ProgramCalendarView): { amount: number; unit: 'day' | 'week' | 'month' } => {
  if (view === 'month') return { amount: 1, unit: 'month' };
  if (view === 'agenda') return { amount: 1, unit: 'day' };
  return { amount: 1, unit: 'week' };
};

interface AthletesTabProps {
  /** Re-selects the athlete a coach was on before creating a workout. */
  initialAthleteId?: string;
  view: ProgramCalendarView;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  calendarMaxDate?: Date;
}

const AthletesTab = ({
  initialAthleteId,
  view,
  setView,
  date,
  setDate,
  calendarMaxDate,
}: AthletesTabProps): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { successNotification, errorNotification } = notificationHandler();
  const visibilityMutation = useToggleWorkoutVisibility();
  const deleteMutation = useDeleteWorkout();
  const {
    trackAthleteSelected,
    trackAthleteListPanelToggled,
    trackCalendarViewChanged,
    trackAssignWorkoutOpened,
    trackWorkoutCardClicked,
    trackWorkoutVisibilityQuickToggled,
    trackWorkoutEditClicked,
    trackWorkoutDeleted,
  } = useStaffTracking();
  const { canMutate } = useAssignGate();

  const [athlete, setAthlete] = useState<AthleteCalendarAthlete>();
  // null date means the modal shows the ASSIGN TO DATE pill — only the info bar entry.
  const [assignDate, setAssignDate] = useState<string | null>(null);
  const [assignOpened, setAssignOpened] = useState(false);
  const [previewWorkoutId, setPreviewWorkoutId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ event: AthleteCalendarEventItem; date: string } | null>(null);
  const [replacesWorkoutId, setReplacesWorkoutId] = useState<string>();
  const [replacementVisibleToAthletes, setReplacementVisibleToAthletes] = useState(true);
  const [workoutPendingDelete, setWorkoutPendingDelete] = useState<AthleteCalendarWorkoutItem | null>(null);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [filters, setFilters] = useState<AthleteCalendarFilters>({
    showClasses: true,
    showAppointments: true,
  });

  const { start, end } = rangeForView(view, date);
  const { itemsByDate, isLoading, isError } = useAthleteCalendar({
    athleteId: athlete?.athleteId,
    startDate: start,
    endDate: end,
    showClasses: filters.showClasses,
    showAppointments: filters.showAppointments,
  });

  const restoredAthlete = useAthlete(initialAthleteId);

  useEffect(() => {
    const fetched = restoredAthlete.data?.data;
    if (!initialAthleteId || athlete || !fetched) return;

    setAthlete(fetched);
  }, [initialAthleteId, athlete, restoredAthlete.data]);

  const handleSelectAthlete = (selected: AthleteList): void => {
    setAthlete(selected);
    trackAthleteSelected({ athlete_id: selected.athleteId, package_name: packageNames(selected) });
  };

  const handleTogglePanel = (): void => {
    const collapsed = !panelCollapsed;
    setPanelCollapsed(collapsed);
    trackAthleteListPanelToggled({ state: collapsed ? 'collapsed' : 'expanded' });
  };

  const handleViewChange = (next: ProgramCalendarView): void => {
    if (next === view) return;
    trackCalendarViewChanged({ view: viewLabel(next), previous_view: viewLabel(view) });
    setView(next);
  };

  const step = stepForView(view);

  const closeDrawer = (): void => setDrawer(null);

  const openAssignModal = (forDate: string | null, source: AssignSource): void => {
    closeDrawer();
    setReplacesWorkoutId(undefined);
    setReplacementVisibleToAthletes(true);
    setAssignDate(forDate);
    setAssignOpened(true);
    trackAssignWorkoutOpened({ source });
  };

  const handleToggleVisibility = (item: AthleteCalendarWorkoutItem): void => {
    const next = !item.isVisibleToAthletes;
    trackWorkoutVisibilityQuickToggled({
      workout_id: item.id,
      new_state: next ? 'visible' : 'hidden',
      view: viewLabel(view),
    });

    visibilityMutation.mutate(
      { workoutId: item.id, isVisibleToAthletes: next },
      {
        onSuccess: () => {
          successNotification(
            next ? 'Workout shown' : 'Workout hidden',
            next
              ? `${item.name} is visible to this athlete again.`
              : `${item.name} is no longer visible to this athlete.`,
          );
        },
        onError: error => {
          errorNotification('Update failed', handleApiError(error).message);
        },
      },
    );
  };

  const handleOpenProfile = (selected: AthleteCalendarAthlete): void => {
    const returnState: ProgrammingNavState = { tab: 'athletes', athleteId: selected.athleteId, view, date };
    navigate('/workouts/programming', { replace: true, state: returnState });
    navigate(`/users/athletes/${selected.athleteId}`);
  };

  const handleEditWorkout = (item: AthleteCalendarWorkoutItem, itemDate: string): void => {
    trackWorkoutEditClicked({ workout_id: item.id, source: 'menu' });
    navigate(`/workouts/programming/edit/${item.id}`, {
      state: { date: itemDate, view: view === 'week' ? 'week' : 'agenda' },
    });
  };

  const handleChangeWorkout = (item: AthleteCalendarWorkoutItem, itemDate: string): void => {
    closeDrawer();
    setReplacesWorkoutId(item.id);
    setReplacementVisibleToAthletes(item.isVisibleToAthletes);
    setAssignDate(itemDate);
    setAssignOpened(true);
    trackAssignWorkoutOpened({ source: 'change_workout' });
  };

  const handleConfirmDelete = (): void => {
    if (!workoutPendingDelete) return;

    deleteMutation.mutate(workoutPendingDelete.id, {
      onSuccess: () => {
        trackWorkoutDeleted({ workout_id: workoutPendingDelete.id });
        successNotification('Workout deleted', `${workoutPendingDelete.name} was removed.`);
        setWorkoutPendingDelete(null);
      },
      onError: error => {
        errorNotification('Delete failed', handleApiError(error).message);
      },
    });
  };

  const renderWorkoutMenu = (item: AthleteCalendarWorkoutItem, itemDate: string): React.ReactNode => {
    if (item.source !== 'athlete' || !canMutate) return null;

    return (
      <WorkoutAssignmentMenu
        canMutate={canMutate}
        hasWorkoutId
        owner='athlete'
        isVisibleToAthletes={item.isVisibleToAthletes}
        isLocked={item.isLogged}
        onToggleVisibility={() => handleToggleVisibility(item)}
        onEditWorkout={() => handleEditWorkout(item, itemDate)}
        onChangeWorkout={() => handleChangeWorkout(item, itemDate)}
        onDeleteWorkout={() => setWorkoutPendingDelete(item)}
        target={
          <ActionIcon
            variant='subtle'
            size='sm'
            aria-label='Workout assignment options'
            onClick={event => event.stopPropagation()}
            data-cy='athleteWorkoutMenu'
          >
            <IconDotsVertical size={16} />
          </ActionIcon>
        }
      />
    );
  };

  const handleItemClick = (item: AthleteCalendarItem, itemDate: string): void => {
    if (item.kind === 'workout') {
      trackWorkoutCardClicked({ workout_id: item.id });
      setPreviewWorkoutId(item.id);
      return;
    }

    setDrawer({ event: item, date: itemDate });
  };

  const handleDaySelect = (nextDate: string): void => {
    setDate(nextDate);
    handleViewChange('agenda');
  };

  return (
    <Box className={classes.layout} data-cy='athletesTab'>
      <Box className={classes.panelSlot} sx={{ width: panelCollapsed ? 0 : PANEL_WIDTH }}>
        <AthletePanel selectedAthleteId={athlete?.athleteId} onSelectAthlete={handleSelectAthlete} />
      </Box>

      <Box className={classes.calendar}>
        {!athlete ? (
          <Center mih={400}>
            <Stack align='center' spacing='sm' c='gray.6'>
              <IconUsers size={32} stroke={1.5} />
              <Text size='sm'>Select an athlete to view their schedule and assigned workouts.</Text>
              {panelCollapsed && (
                <KiloButton variant='light' color='kiloBlue' size='xs' onClick={handleTogglePanel}>
                  Show athlete list
                </KiloButton>
              )}
            </Stack>
          </Center>
        ) : (
          <>
            <AthleteInfoBar
              athlete={athlete}
              onOpenProfile={() => handleOpenProfile(athlete)}
              onAssignWorkout={canMutate ? () => openAssignModal(null, 'info_bar') : undefined}
            />
            <AthleteCalendarHeader
              view={view}
              onViewChange={handleViewChange}
              periodLabel={periodLabelForView(view, date)}
              onPrev={() => setDate(dayjs(date).subtract(step.amount, step.unit).format('YYYY-MM-DD'))}
              onNext={() => setDate(dayjs(date).add(step.amount, step.unit).format('YYYY-MM-DD'))}
              onToday={() => setDate(dayjs().format('YYYY-MM-DD'))}
              panelCollapsed={panelCollapsed}
              onTogglePanel={handleTogglePanel}
              filters={filters}
              onFiltersChange={setFilters}
            />

            <Box className={classes.calendarBody}>
              {isError ? (
                <Text p='sm'>Something went wrong</Text>
              ) : isLoading ? (
                <Center mih={300}>
                  <Loader />
                </Center>
              ) : view === 'week' ? (
                <AthleteWeekView
                  weekStart={start}
                  itemsByDate={itemsByDate}
                  onItemClick={handleItemClick}
                  onAssignWorkout={day => openAssignModal(day, 'week_view')}
                  canAssign={canMutate}
                  renderWorkoutMenu={renderWorkoutMenu}
                />
              ) : view === 'month' ? (
                <AthleteMonthView
                  date={date}
                  itemsByDate={itemsByDate}
                  onDaySelect={handleDaySelect}
                  onAssignWorkout={day => openAssignModal(day, 'month_view')}
                  canAssign={canMutate}
                  calendarMaxDate={calendarMaxDate}
                />
              ) : (
                <AthleteDayView
                  date={date}
                  itemsByDate={itemsByDate}
                  onItemClick={handleItemClick}
                  onAssignWorkout={day => openAssignModal(day, 'day_view')}
                  canAssign={canMutate}
                  renderWorkoutMenu={renderWorkoutMenu}
                />
              )}
            </Box>

            <AssignWorkoutModal
              opened={assignOpened}
              onClose={() => setAssignOpened(false)}
              target={{ kind: 'athlete', id: athlete.athleteId, name: athlete.fullName }}
              date={assignDate ?? undefined}
              view={view === 'week' ? 'week' : 'agenda'}
              replacesWorkoutId={replacesWorkoutId}
              defaultVisibleToAthletes={replacementVisibleToAthletes}
            />

            <WorkoutPreviewModal
              opened={previewWorkoutId !== null}
              onClose={() => setPreviewWorkoutId(null)}
              workoutId={previewWorkoutId}
              returnView={view === 'week' ? 'week' : 'agenda'}
            />

            {workoutPendingDelete && (
              <DeleteWorkoutModal
                opened
                onClose={() => setWorkoutPendingDelete(null)}
                onConfirm={handleConfirmDelete}
                isLoading={deleteMutation.isLoading}
                workoutId={workoutPendingDelete.id}
                workoutName={workoutPendingDelete.name}
              />
            )}

            <AthleteEventDrawer
              opened={drawer !== null}
              onClose={closeDrawer}
              item={drawer?.event ?? null}
              date={drawer?.date ?? null}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default AthletesTab;
